import styled from 'styled-components';
import Grid from '@mui/material/Grid';
import Card from '@mui/material/Card';
import { ArrowForwardRounded } from '@mui/icons-material';
import { ExtendButtonBase } from '@mui/material/ButtonBase';
import CardActionArea, { CardActionAreaTypeMap } from '@mui/material/CardActionArea';
import Chip from '@mui/material/Chip';
import Typography from '@mui/material/Typography';
import { useTheme } from '@mui/material/styles';
import { ColorCode } from './TriageHeading';
import TreatmentPlan from './TreatmentPlan';
import { TREATMENT_PLAN_FEATURE_FLAG } from './TreatmentPlan/constants';
import { IS_LOCAL } from '../requests/settings';

import useInterviewType from 'src/interviews/hooks/useInterviewType';
import useFeatureFlag from 'src/hooks/useFeatureFlag';

import type { IDiagnosis } from 'src/packages/polaris/types';

const TriageLevelIndicator = styled(ColorCode)<{ level: number }>`
  display: inline-block;
  width: 12px;
  height: 12px;
  flex-shrink: 0;
  margin-top: 2px;
  background-color: ${({ theme, level }) => {
    switch (level) {
      case 7:
      case 6:
        return theme.palette.status.error;
      case 5:
      case 4:
        return theme.palette.orange[20];
      default:
        return theme.palette.teal[40];
    }
  }};
`;

const StyledLink = styled.span`
  ${({ theme }) => theme.typography.variants.primary.small};
  color: ${({ theme }) => theme.palette.primary.main};
  font-size: 14px;
  display: flex;
  align-items: center;
  margin-top: 6px;

  svg {
    margin-top: 4px;
  }
`;

const StyledCardActionArea: ExtendButtonBase<CardActionAreaTypeMap<unknown, 'button'>> = styled(
  CardActionArea,
)`
  &:hover ${StyledLink} {
    text-decoration: underline;
    color: ${({ theme }) => theme.palette.blue[70]};
  }
`;

export const getTriageHeading = (diagnosis: IDiagnosis) => {
  const { triage } = diagnosis;

  if (!triage?.level && triage?.level !== 0) {
    return '';
  }

  switch (triage.level) {
    case 0:
      return 'Usually resolves on its own.';
    case 1:
      return 'Usually self-treatable.';
    case 2:
      return 'Recommend a virtual or in-person visit in the next 3 days.';
    case 3:
      return 'See a primary care doctor within 2 weeks.';
    case 4:
      return 'See a primary care doctor within 1-2 days.';
    case 5:
      return 'Recommend an in-person primary care or urgent care visit; today or as soon as possible.';
    case 6:
      return 'This condition requires immediate medical care.';
    case 7:
      return 'This condition is an emergency that could be life threatening. Consider calling 911.';
    default:
      return triage.description || '';
  }
};

const DifferentialCard = ({
  differential,
  showTreatmentPlan,
  className,
}: {
  differential: IDiagnosis;
  /** Adds a button to the bottom of the card that links user to a treatment plan for this dx */
  showTreatmentPlan?: boolean;
  className?: string;
}) => {
  const interviewType = useInterviewType();
  const theme = useTheme();
  const { belief, name, could_explain, content_page, triage } = differential;
  const treatmentPlanFlagEnabled = useFeatureFlag({
    flagName: TREATMENT_PLAN_FEATURE_FLAG,
    defaultValue: IS_LOCAL,
  });
  const pillColor = (belief: string): string => {
    switch (belief) {
      case 'Best match':
      case 'likely':
        return theme.palette.teal[20];
      case 'Unlikely':
        return theme.palette.red[20];
      default:
        return theme.palette.orange[10];
    }
  };
  return (
    <Grid
      className={`BuoyInterviews__DifferentialCard bp-h-full ${className}`}
      item
      sx={{ width: '100%' }}
    >
      <Card variant="outlined" className="bp-flex bp-h-full bp-flex-col bp-shadow-sm">
        <StyledCardActionArea
          className="bp-flex bp-h-full bp-items-stretch"
          disabled={!content_page}
          href={`/learn${content_page}`}
          target="_blank"
        >
          <Grid
            container
            direction="column"
            spacing={1}
            sx={{ padding: 2.5 }}
            className="bp-flex bp-flex-1 bp-flex-nowrap"
          >
            {belief && (
              <Grid item>
                <Chip
                  label={belief}
                  sx={{
                    padding: '0',
                    marginBottom: '4px',
                    height: '18px',
                    fontSize: '12px',
                    textTransform: 'capitalize',
                    backgroundColor: pillColor(belief),
                    '>span': {
                      padding: '10px',
                    },
                  }}
                />
              </Grid>
            )}
            <Grid item paddingBottom={1}>
              <Typography sx={{ fontSize: '18px', lineHeight: '120%' }} variant="h6">
                {content_page ? (
                  <span className="bp-flex bp-items-center">
                    <span className="bp-mr-1 bp-text-blue-50 bp-underline bp-underline-offset-2">
                      {name}
                    </span>
                    <ArrowForwardRounded color="primary" fontSize="inherit" />
                  </span>
                ) : (
                  name
                )}
              </Typography>
              {/* eslint-disable-next-line eqeqeq */}
              {triage.level != undefined && (
                <Typography
                  sx={{ fontSize: '14px', lineHeight: '120%', display: 'flex' }}
                  variant="body2"
                  className="bp-mt-1"
                >
                  <TriageLevelIndicator level={triage.level} />
                  {getTriageHeading(differential)}
                </Typography>
              )}
            </Grid>
            <Grid item>
              <Typography sx={{ fontSize: '14px', lineHeight: '120%' }} variant="body2">
                {could_explain}
              </Typography>
            </Grid>
          </Grid>
        </StyledCardActionArea>
        {showTreatmentPlan && treatmentPlanFlagEnabled && Boolean(differential.predictor) && (
          <TreatmentPlan differential={differential} />
        )}
      </Card>
    </Grid>
  );
};

export default DifferentialCard;
